import { create } from 'zustand';
import { gradeShortAnswer } from '../../../PreLabAssessment';

export type PartId = 'A' | 'B' | 'C';
export type PartStage =
  | 'setup'
  | 'reactant-1'
  | 'reactant-2'
  | 'apparatus-built'
  | 'reacting'
  | 'observed'
  | 'answered';

export type CameraView = 'default' | 'top' | 'side' | 'closeup' | 'workbench';

export interface PartPreset {
  title: string;
  reactantsLabel: string;
  canonicalEquation: string;
  canonicalObservation: string;
  reactionType: 'thermal-decomp' | 'gas-reaction' | 'precipitation';
}

export const PART_PRESETS: Record<PartId, PartPreset> = {
  A: {
    title: 'Heating Copper(II) Carbonate',
    reactantsLabel: 'CuCO₃ + heat',
    canonicalEquation: 'CuCO3(s) -> CuO(s) + CO2(g)',
    canonicalObservation: 'Green CuCO3 turns black (CuO) and CO2 gas turns limewater milky.',
    reactionType: 'thermal-decomp',
  },
  B: {
    title: 'Ammonia + Hydrogen Chloride',
    reactantsLabel: 'HCl(g) + NH₃(g)',
    canonicalEquation: 'HCl(g) + NH3(g) -> NH4Cl(s)',
    canonicalObservation: 'White smoke of ammonium chloride forms between the test tubes.',
    reactionType: 'gas-reaction',
  },
  C: {
    title: 'Lead(II) Nitrate + Potassium Iodide',
    reactantsLabel: 'Pb(NO₃)₂(aq) + KI(aq)',
    canonicalEquation: 'Pb(NO3)2(aq) + 2KI(aq) -> PbI2(s) + 2KNO3(aq)',
    canonicalObservation: 'Bright yellow precipitate of lead(II) iodide forms instantly.',
    reactionType: 'precipitation',
  },
};

export interface PartAnswer {
  observation: string;
  equation: string;
  submitted: boolean;
  observationCorrect: boolean;
  equationCorrect: boolean;
}

export interface PartResult {
  part: PartId;
  reactantsLabel: string;
  studentObservation: string;
  studentEquation: string;
  observationCorrect: boolean;
  equationCorrect: boolean;
  canonicalEquation: string;
  canonicalObservation: string;
  recordedAt: number;
}

// =====================================================
// Equation grader
// =====================================================

function normalizeEquation(eq: string): string {
  return eq
    .toLowerCase()
    .replace(/[→⇌=]/g, '->')
    .replace(/\(s\)|\(l\)|\(g\)|\(aq\)/g, '')
    .replace(/\s+/g, '')
    .replace(/[·•]/g, '·');
}

interface Token { coeff: number; formula: string }

function tokenizeFormulas(side: string): Token[] {
  return side.split('+').map((raw): Token => {
    const t = raw.trim();
    if (!t) return { coeff: 0, formula: '' };
    const m = t.match(/^(\d*)(.+)$/);
    if (m) {
      const coeff = m[1] ? parseInt(m[1], 10) : 1;
      return { coeff, formula: m[2] };
    }
    return { coeff: 1, formula: t };
  }).filter(t => t.formula);
}

function multisetEqual(a: Token[], b: Token[]): boolean {
  if (a.length !== b.length) return false;
  const left = [...b];
  for (const x of a) {
    const idx = left.findIndex(y => y.formula === x.formula && y.coeff === x.coeff);
    if (idx < 0) return false;
    left.splice(idx, 1);
  }
  return true;
}

export function gradeEquation(canonical: string, user: string): boolean {
  const c = normalizeEquation(canonical);
  const u = normalizeEquation(user);
  if (!u) return false;
  if (c === u) return true;
  const [cL, cR] = c.split('->');
  const [uL, uR] = u.split('->');
  if (!cL || !cR || !uL || !uR) return false;
  return (
    multisetEqual(tokenizeFormulas(cL), tokenizeFormulas(uL)) &&
    multisetEqual(tokenizeFormulas(cR), tokenizeFormulas(uR))
  );
}

// =====================================================
// Store
// =====================================================

interface BEState {
  currentPart: PartId;
  partStage: Record<PartId, PartStage>;

  // Visual state per part
  cuCO3HeatingProgress: number;
  limewaterMilkyAmount: number;
  smokeFormationAmount: number;
  pbI2FormationProgress: number;
  tubesAreTouching: boolean;
  hclPresent: boolean;
  nh3Present: boolean;
  pbno3Volume: number;
  kiVolume: number;
  mixedTubeContents: 'empty' | 'pb' | 'mixed';
  isHeating: boolean;
  flameIntensity: number;
  cuco3TempC: number;

  // Animation flags / progress
  isAddingReactant1: boolean; reactant1Progress: number;
  isAddingReactant2: boolean; reactant2Progress: number;
  isBuildingApparatus: boolean; buildProgress: number;
  isBringingTubesTogether: boolean; bringTogetherProgress: number;
  isPouringMix: boolean; pourMixProgress: number;
  isShaking: boolean; shakeProgress: number;

  partAnswers: Record<PartId, PartAnswer>;
  results: PartResult[];

  experimentTime: number;
  simulationSpeed: number;
  cameraView: CameraView;
  cameraVersion: number;
  showTutorial: boolean;

  // Actions
  selectPart: (p: PartId) => void;
  // Part A
  addCuCO3: () => void;
  buildPartAApparatus: () => void;
  togglePartAHeating: () => void;
  // Part B
  addHCl: () => void;
  addNH3: () => void;
  removeStoppersAndBringTogether: () => void;
  separateTubes: () => void;
  // Part C
  addPbNO3: () => void;
  addKI: () => void;
  pourKIintoPbNO3: () => void;
  shakeMixedTube: () => void;
  // Common
  updateAnswerDraft: (part: PartId, field: 'observation' | 'equation', value: string) => void;
  submitAnswer: (part: PartId) => void;
  resetPart: (part: PartId) => void;
  resetAll: () => void;
  setSimulationSpeed: (s: number) => void;
  cycleSimulationSpeed: () => void;
  setCameraView: (v: CameraView) => void;
  setShowTutorial: (show: boolean) => void;
  tick: (delta: number) => void;
}

const SPEED_CYCLE = [1, 2, 5, 10];
const DECOMP_DURATION = 6.0;
const MILKY_DURATION = 5.0;
const SMOKE_BUILD_DURATION = 2.5;
const SMOKE_FADE_DURATION = 2.0;
const PRECIPITATE_DURATION = 0.8;
const REACT1_DURATION = 1.6;
const REACT2_DURATION = 1.6;
const BUILD_DURATION = 2.0;
const BRING_TOGETHER_DURATION = 2.4;
const POUR_MIX_DURATION = 2.4;
const SHAKE_DURATION = 1.6;
const IGNITION_TEMP_C = 280;
const TUBE_MAX_HEAT_C = 700;

function emptyAnswer(): PartAnswer {
  return {
    observation: '',
    equation: '',
    submitted: false,
    observationCorrect: false,
    equationCorrect: false,
  };
}

function freshPartState() {
  return {
    cuCO3HeatingProgress: 0,
    limewaterMilkyAmount: 0,
    smokeFormationAmount: 0,
    pbI2FormationProgress: 0,
    tubesAreTouching: false,
    hclPresent: false,
    nh3Present: false,
    pbno3Volume: 0,
    kiVolume: 0,
    mixedTubeContents: 'empty' as const,
    isHeating: false,
    flameIntensity: 0.9,
    cuco3TempC: 25,
    isAddingReactant1: false, reactant1Progress: 0,
    isAddingReactant2: false, reactant2Progress: 0,
    isBuildingApparatus: false, buildProgress: 0,
    isBringingTubesTogether: false, bringTogetherProgress: 0,
    isPouringMix: false, pourMixProgress: 0,
    isShaking: false, shakeProgress: 0,
  };
}

export const useBalancedEquationsStore = create<BEState>((set, get) => ({
  currentPart: 'A',
  partStage: { A: 'setup', B: 'setup', C: 'setup' },
  ...freshPartState(),
  partAnswers: { A: emptyAnswer(), B: emptyAnswer(), C: emptyAnswer() },
  results: [],
  experimentTime: 0,
  simulationSpeed: 1,
  cameraView: 'default',
  cameraVersion: 0,
  showTutorial: false,

  selectPart: (p) => set((s) => ({ currentPart: p, cameraVersion: s.cameraVersion + 1 })),

  // ----- PART A -----
  addCuCO3: () => set((s) => {
    if (s.currentPart !== 'A' || s.partStage.A !== 'setup' || s.isAddingReactant1) return {};
    return { isAddingReactant1: true, reactant1Progress: 0 };
  }),

  buildPartAApparatus: () => set((s) => {
    if (s.currentPart !== 'A' || s.partStage.A !== 'reactant-1' || s.isBuildingApparatus) return {};
    return { isBuildingApparatus: true, buildProgress: 0 };
  }),

  togglePartAHeating: () => set((s) => {
    if (s.currentPart !== 'A') return {};
    if (s.partStage.A === 'apparatus-built' && !s.isHeating) {
      return {
        isHeating: true,
        partStage: { ...s.partStage, A: 'reacting' },
      };
    }
    if (s.partStage.A === 'reacting' && s.isHeating) {
      // Allow stopping once reaction is visibly done
      if (s.cuCO3HeatingProgress < 0.95) return {};
      return {
        isHeating: false,
        partStage: { ...s.partStage, A: 'observed' },
      };
    }
    return {};
  }),

  // ----- PART B -----
  addHCl: () => set((s) => {
    if (s.currentPart !== 'B' || s.partStage.B !== 'setup' || s.isAddingReactant1) return {};
    return { isAddingReactant1: true, reactant1Progress: 0 };
  }),

  addNH3: () => set((s) => {
    if (s.currentPart !== 'B' || s.partStage.B !== 'reactant-1' || s.isAddingReactant2) return {};
    return { isAddingReactant2: true, reactant2Progress: 0 };
  }),

  removeStoppersAndBringTogether: () => set((s) => {
    if (s.currentPart !== 'B' || s.partStage.B !== 'reactant-2' || s.isBringingTubesTogether) return {};
    return {
      isBringingTubesTogether: true,
      bringTogetherProgress: 0,
    };
  }),

  separateTubes: () => set((s) => {
    if (s.currentPart !== 'B' || s.partStage.B !== 'reacting') return {};
    if (s.smokeFormationAmount < 0.6) return {};
    return {
      tubesAreTouching: false,
      partStage: { ...s.partStage, B: 'observed' },
    };
  }),

  // ----- PART C -----
  addPbNO3: () => set((s) => {
    if (s.currentPart !== 'C' || s.partStage.C !== 'setup' || s.isAddingReactant1) return {};
    return { isAddingReactant1: true, reactant1Progress: 0 };
  }),

  addKI: () => set((s) => {
    if (s.currentPart !== 'C' || s.partStage.C !== 'reactant-1' || s.isAddingReactant2) return {};
    return { isAddingReactant2: true, reactant2Progress: 0 };
  }),

  pourKIintoPbNO3: () => set((s) => {
    if (s.currentPart !== 'C' || s.partStage.C !== 'reactant-2' || s.isPouringMix) return {};
    return { isPouringMix: true, pourMixProgress: 0 };
  }),

  shakeMixedTube: () => set((s) => {
    if (s.currentPart !== 'C' || s.partStage.C !== 'reacting' || s.isShaking) return {};
    if (s.pbI2FormationProgress < 0.95) return {};
    return { isShaking: true, shakeProgress: 0 };
  }),

  // ----- COMMON -----
  updateAnswerDraft: (part, field, value) => set((s) => ({
    partAnswers: {
      ...s.partAnswers,
      [part]: { ...s.partAnswers[part], [field]: value },
    },
  })),

  submitAnswer: (part) => set((s) => {
    if (s.partStage[part] !== 'observed' && s.partStage[part] !== 'answered') return {};
    const draft = s.partAnswers[part];
    if (!draft.observation.trim() || !draft.equation.trim()) return {};
    const preset = PART_PRESETS[part];
    const observationCorrect = gradeShortAnswer(preset.canonicalObservation, draft.observation);
    const equationCorrect = gradeEquation(preset.canonicalEquation, draft.equation);
    const newAnswer: PartAnswer = {
      ...draft,
      submitted: true,
      observationCorrect,
      equationCorrect,
    };
    const newResult: PartResult = {
      part,
      reactantsLabel: preset.reactantsLabel,
      studentObservation: draft.observation.trim(),
      studentEquation: draft.equation.trim(),
      observationCorrect,
      equationCorrect,
      canonicalEquation: preset.canonicalEquation,
      canonicalObservation: preset.canonicalObservation,
      recordedAt: Math.floor(s.experimentTime),
    };
    const filteredResults = s.results.filter(r => r.part !== part);
    return {
      partAnswers: { ...s.partAnswers, [part]: newAnswer },
      partStage: { ...s.partStage, [part]: 'answered' },
      results: [...filteredResults, newResult],
    };
  }),

  resetPart: (part) => set((s) => ({
    partStage: { ...s.partStage, [part]: 'setup' },
    partAnswers: { ...s.partAnswers, [part]: emptyAnswer() },
    results: s.results.filter(r => r.part !== part),
    // Reset the visual state if we're resetting the current part
    ...(part === s.currentPart ? freshPartState() : {}),
  })),

  resetAll: () => set({
    currentPart: 'A',
    partStage: { A: 'setup', B: 'setup', C: 'setup' },
    ...freshPartState(),
    partAnswers: { A: emptyAnswer(), B: emptyAnswer(), C: emptyAnswer() },
    results: [],
    experimentTime: 0,
    simulationSpeed: 1,
  }),

  setSimulationSpeed: (s) => set({ simulationSpeed: Math.max(1, Math.min(10, s)) }),

  cycleSimulationSpeed: () => set((state) => {
    const idx = SPEED_CYCLE.indexOf(state.simulationSpeed);
    return { simulationSpeed: SPEED_CYCLE[(idx + 1) % SPEED_CYCLE.length] };
  }),

  setCameraView: (v) => set((s) => ({ cameraView: v, cameraVersion: s.cameraVersion + 1 })),
  setShowTutorial: (show) => set({ showTutorial: show }),

  tick: (delta) => set((s) => {
    const realDelta = delta / Math.max(0.0001, s.simulationSpeed);
    const out: Partial<BEState> = {};
    out.experimentTime = s.experimentTime + delta;

    // ---- Reactant-1 animation completes the "reactant-1" stage ----
    if (s.isAddingReactant1) {
      const p = Math.min(1, s.reactant1Progress + realDelta / REACT1_DURATION);
      if (p >= 1) {
        out.isAddingReactant1 = false;
        out.reactant1Progress = 1;
        if (s.currentPart === 'A') {
          out.partStage = { ...s.partStage, A: 'reactant-1' };
        } else if (s.currentPart === 'B') {
          out.hclPresent = true;
          out.partStage = { ...s.partStage, B: 'reactant-1' };
        } else if (s.currentPart === 'C') {
          out.pbno3Volume = 2.0;
          out.mixedTubeContents = 'pb';
          out.partStage = { ...s.partStage, C: 'reactant-1' };
        }
      } else {
        out.reactant1Progress = p;
      }
    }

    // ---- Reactant-2 animation completes the "reactant-2" stage (Parts B & C only) ----
    if (s.isAddingReactant2) {
      const p = Math.min(1, s.reactant2Progress + realDelta / REACT2_DURATION);
      if (p >= 1) {
        out.isAddingReactant2 = false;
        out.reactant2Progress = 1;
        if (s.currentPart === 'B') {
          out.nh3Present = true;
          out.partStage = { ...s.partStage, B: 'reactant-2' };
        } else if (s.currentPart === 'C') {
          out.kiVolume = 2.0;
          out.partStage = { ...s.partStage, C: 'reactant-2' };
        }
      } else {
        out.reactant2Progress = p;
      }
    }

    // ---- Part A apparatus build ----
    if (s.isBuildingApparatus) {
      const p = Math.min(1, s.buildProgress + realDelta / BUILD_DURATION);
      if (p >= 1) {
        out.isBuildingApparatus = false;
        out.buildProgress = 1;
        out.partStage = { ...s.partStage, A: 'apparatus-built' };
      } else {
        out.buildProgress = p;
      }
    }

    // ---- Part B bring tubes together ----
    if (s.isBringingTubesTogether) {
      const p = Math.min(1, s.bringTogetherProgress + realDelta / BRING_TOGETHER_DURATION);
      if (p >= 1) {
        out.isBringingTubesTogether = false;
        out.bringTogetherProgress = 1;
        out.tubesAreTouching = true;
        out.partStage = { ...s.partStage, B: 'reacting' };
      } else {
        out.bringTogetherProgress = p;
      }
    }

    // ---- Part C pour KI into Pb(NO3)2 ----
    if (s.isPouringMix) {
      const p = Math.min(1, s.pourMixProgress + realDelta / POUR_MIX_DURATION);
      if (s.pourMixProgress < 0.7 && p >= 0.7) {
        out.mixedTubeContents = 'mixed';
        out.partStage = { ...s.partStage, C: 'reacting' };
      }
      if (p >= 1) {
        out.isPouringMix = false;
        out.pourMixProgress = 1;
      } else {
        out.pourMixProgress = p;
      }
    }

    // ---- Part C shake animation ----
    if (s.isShaking) {
      const p = Math.min(1, s.shakeProgress + realDelta / SHAKE_DURATION);
      if (p >= 1) {
        out.isShaking = false;
        out.shakeProgress = 1;
        out.partStage = { ...s.partStage, C: 'observed' };
      } else {
        out.shakeProgress = p;
      }
    }

    // ---- Part A heating / reduction dynamics ----
    let nextTemp = s.cuco3TempC;
    if (s.isHeating) {
      const target = 200 + s.flameIntensity * (TUBE_MAX_HEAT_C - 200);
      nextTemp += (target - s.cuco3TempC) * 0.30 * delta;
      if (nextTemp > TUBE_MAX_HEAT_C) nextTemp = TUBE_MAX_HEAT_C;
    } else if (nextTemp > 25) {
      nextTemp -= Math.max(0, (s.cuco3TempC - 25)) * 0.30 * delta;
      if (nextTemp < 25) nextTemp = 25;
    }
    out.cuco3TempC = nextTemp;

    // Decomposition progresses while heating above ignition temp
    if (s.partStage.A === 'reacting' && s.isHeating && nextTemp > IGNITION_TEMP_C) {
      const nextHeat = Math.min(1, s.cuCO3HeatingProgress + delta / DECOMP_DURATION);
      out.cuCO3HeatingProgress = nextHeat;
      // Limewater turns milky a bit AFTER the decomposition starts
      if (nextHeat > 0.10) {
        const startedAt = 0.10;
        const localT = Math.min(1, (nextHeat - startedAt) / 0.55);
        out.limewaterMilkyAmount = localT;
      }
    }

    // ---- Part B smoke formation / fade ----
    if (s.currentPart === 'B') {
      if (s.tubesAreTouching && s.hclPresent && s.nh3Present) {
        out.smokeFormationAmount = Math.min(1, s.smokeFormationAmount + delta / SMOKE_BUILD_DURATION);
      } else if (s.smokeFormationAmount > 0) {
        out.smokeFormationAmount = Math.max(0, s.smokeFormationAmount - delta / SMOKE_FADE_DURATION);
      }
    }

    // ---- Part C precipitate formation ----
    if (s.currentPart === 'C' && s.mixedTubeContents === 'mixed' && s.pbI2FormationProgress < 1) {
      out.pbI2FormationProgress = Math.min(1, s.pbI2FormationProgress + delta / PRECIPITATE_DURATION);
    }

    return out;
  }),
}));
